import { useState } from 'react';
import { ErrorUtils } from '../../../../common/utils/services/error-utils';
import { CreatePdfReportRequest } from '../../../../common/model/dto/pdf-report/create-pdf-report-request';
import { UpdatePdfReportRequest } from '../../../../common/model/dto/pdf-report/update-pdf-report-request';
import { UserInfo } from '../../../../common/model/dto/user-info';
import {
  createPdfReport,
  deletePdfReport,
  updatePdfReport,
} from '../../../../services/admin/admin-pdf-report.service';
import {
  createPdfReportForPatient,
  deletePdfReportForPatient,
  updatePdfReportForPatient,
} from '../../../../services/pdf-report.service';

interface UsePdfReportActionsParams {
  clinicLocationId?: string;
  selectedUser?: UserInfo;
  refetch: () => void;
}

export const usePdfReportActions = ({
  clinicLocationId,
  selectedUser,
  refetch,
}: UsePdfReportActionsParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const executeAction = async (
    clinicAction: () => Promise<any>,
    adminAction: () => Promise<any>
  ) => {
    try {
      setErrorMessage('');
      setIsLoading(true);
      clinicLocationId ? await clinicAction() : await adminAction();
      refetch();
    } catch (error: unknown) {
      setErrorMessage(ErrorUtils.getApiErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const createReport = async (
    user: UserInfo,
    request: CreatePdfReportRequest
  ) => {
    await executeAction(
      () => createPdfReportForPatient(clinicLocationId!, user.id, request),
      () => createPdfReport(user.id, request)
    );
  };

  const updateReport = async (updateObj: {
    id: string;
    updatedProperties: UpdatePdfReportRequest;
  }) => {
    if (!selectedUser) return;

    await executeAction(
      () =>
        updatePdfReportForPatient(
          clinicLocationId!,
          selectedUser.id,
          updateObj.id,
          updateObj.updatedProperties
        ),
      () => updatePdfReport(updateObj.id, updateObj.updatedProperties)
    );
  };

  const deleteReport = async (pdfReportId: string) => {
    if (!selectedUser) return;

    await executeAction(
      () =>
        deletePdfReportForPatient(
          clinicLocationId!,
          selectedUser.id,
          pdfReportId
        ),
      () => deletePdfReport(pdfReportId)
    );
  };

  const clearError = () => setErrorMessage('');

  return {
    createReport,
    updateReport,
    deleteReport,
    isLoading,
    errorMessage,
    clearError,
  };
};
